import Cookies from 'universal-cookie'
import ConfigResolver from '../config/ConfigResolver'

// STY-394: Meta CAPI PageView for react-submissions.
//
// Every user here is an authenticated vendor (email + stable id known
// before any route renders) and ~90% are vendors — the exact paid-
// membership ad target. This is the highest-match-quality PageView source
// we have, and the app had ZERO Meta tracking before this.
//
// react-submissions is served same-origin under www.stylemepretty.com, so
// this POST goes browser -> blog directly: the blog's $_SERVER already has
// the real client IP + browser UA (unlike the nextjs/Express server-to-
// server legs that had to forward them in STY-392/393). We add identity
// (email/external_id) on top; the blog SHA-256-hashes it (never sent raw to
// Meta from here — it's a same-origin first-party call, and the blog hashes
// before dispatch to Meta).
//
// Consent: this is an authenticated B2B vendor tool with no cookie-consent
// banner (none exists); per the established product decision PageViews are
// captured for logged-in users.

const config = ConfigResolver.config()
const cookies = new Cookies()

function generateEventId () {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getCookie (name) {
  try {
    return cookies.get(name) || ''
  } catch (e) {
    return ''
  }
}

function splitName (fullName) {
  if (!fullName || typeof fullName !== 'string') return { firstName: '', lastName: '' }
  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

/**
 * Fire a CAPI PageView for the current URL. Identity-gated: only fires for a
 * resolved, logged-in vendor (the entire value of this surface). Fire-and-
 * forget; never throws into the caller.
 *
 * @param {object} user  the /api/v2/user response object
 * @returns {string} the event_id used (for future Pixel dedup), or ''
 */
export function sendCapiPageView (user) {
  try {
    if (!user || !user.email) return ''
    if (typeof window === 'undefined') return ''

    const eventId = generateEventId()
    const params = new URLSearchParams(window.location.search)
    const fbclid = params.get('fbclid') || ''
    const fbc = getCookie('_fbc') || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : '')
    const { firstName, lastName } = splitName(user.full_name)
    const externalId = String(
      user.id || (user.vendor && user.vendor.id) || ''
    )
    const capiTest =
      params.get('capi_test') === '1' || getCookie('capi_test') === '1'

    const body = {
      event_id: eventId,
      source_url: window.location.href,
      fbp: getCookie('_fbp'),
      fbc,
      fbclid,
      email: user.email,
      firstName,
      lastName,
      external_id: externalId,
      // Browser -> blog is a direct first-party call, so the blog's $_SERVER
      // UA/IP are already the real client's. We still forward the UA
      // explicitly for parity/robustness; client IP is intentionally omitted
      // (cannot be read in-browser; the blog's $_SERVER has the real one).
      client_user_agent: window.navigator ? window.navigator.userAgent : ''
    }

    const headers = { 'Content-Type': 'application/json' }
    if (capiTest) headers['X-CAPI-Test'] = '1'

    // keepalive so an in-flight beacon survives a route change/unload.
    window.fetch(`${config.siteUrl}/api/v2/analytics/pageview`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit'
    }).catch(() => { /* fire-and-forget */ })

    return eventId
  } catch (e) {
    return ''
  }
}

export default sendCapiPageView
